import { create } from 'zustand';
import type { ComposeOutput, ID } from '@proofroom/schema';
import type { LayerOverrides } from '../render/layers';

export type ZoomMode = 'fit' | 'fitWidth' | number;
export type Tool = 'select' | 'pin';
export type Panel = 'editor' | 'threads' | 'changes';

export interface TextRange { paraId: ID; start: number; end: number }

export interface ViewerState {
  draftId?: ID;
  spreadIndex: number;
  zoom: ZoomMode;
  showReference: boolean;
  referenceOpacity: number;
  layerOverrides: LayerOverrides;
  tool: Tool;
  panel: Panel;
  selectedStoryId?: ID;
  caretParaId?: ID;
  selection: TextRange[];
  activeThreadId?: ID;
  composed: Record<ID, ComposeOutput>;
  composing: Record<ID, boolean>;
  composeErrors: Record<ID, string>;
  dirty: Record<ID, true>;
  showFrames: boolean;

  reset(draftId: ID): void;
  setSpread(i: number): void;
  setZoom(z: ZoomMode): void;
  toggleReference(on?: boolean): void;
  setReferenceOpacity(o: number): void;
  setLayerOverrides(o: LayerOverrides): void;
  setTool(t: Tool): void;
  setPanel(p: Panel): void;
  selectStory(id?: ID): void;
  setCaret(paraId?: ID, selection?: TextRange[]): void;
  setActiveThread(id?: ID): void;
  setComposed(storyId: ID, out?: ComposeOutput): void;
  setComposing(storyId: ID, on: boolean): void;
  setComposeError(storyId: ID, message?: string): void;
  markDirty(storyId: ID): void;
  unmarkDirty(storyId: ID): void;
  clearDirty(): void;
  toggleFrames(): void;
}

const initial = {
  spreadIndex: 0,
  zoom: 'fit' as ZoomMode,
  showReference: false,
  referenceOpacity: 0.5,
  layerOverrides: {},
  tool: 'select' as Tool,
  panel: 'editor' as Panel,
  selectedStoryId: undefined,
  caretParaId: undefined,
  selection: [],
  activeThreadId: undefined,
  composed: {},
  composing: {},
  composeErrors: {},
  dirty: {},
  showFrames: false,
};

export const useViewer = create<ViewerState>((set) => ({
  ...initial,
  reset: (draftId) => set({ ...initial, draftId }),
  setSpread: (spreadIndex) => set({ spreadIndex }),
  setZoom: (zoom) => set({ zoom }),
  toggleReference: (on) => set((s) => ({ showReference: on ?? !s.showReference })),
  setReferenceOpacity: (referenceOpacity) => set({ referenceOpacity }),
  setLayerOverrides: (layerOverrides) => set({ layerOverrides }),
  setTool: (tool) => set({ tool }),
  setPanel: (panel) => set({ panel }),
  selectStory: (selectedStoryId) => set((s) => ({ selectedStoryId, panel: selectedStoryId ? 'editor' : s.panel, caretParaId: undefined, selection: [] })),
  setCaret: (caretParaId, selection = []) => set({ caretParaId, selection }),
  setActiveThread: (activeThreadId) => set((s) => ({ activeThreadId, panel: activeThreadId ? 'threads' : s.panel })),
  setComposed: (storyId, out) => set((s) => {
    const composed = { ...s.composed };
    if (out) composed[storyId] = out; else delete composed[storyId];
    return { composed };
  }),
  setComposing: (storyId, on) => set((s) => ({ composing: { ...s.composing, [storyId]: on } })),
  setComposeError: (storyId, message) => set((s) => {
    const composeErrors = { ...s.composeErrors };
    if (message) composeErrors[storyId] = message; else delete composeErrors[storyId];
    return { composeErrors };
  }),
  markDirty: (storyId) => set((s) => (s.dirty[storyId] ? {} : { dirty: { ...s.dirty, [storyId]: true } })),
  unmarkDirty: (storyId) => set((s) => {
    if (!s.dirty[storyId]) return {};
    const dirty = { ...s.dirty };
    delete dirty[storyId];
    return { dirty };
  }),
  clearDirty: () => set({ dirty: {} }),
  toggleFrames: () => set((s) => ({ showFrames: !s.showFrames })),
}));
